import { Check } from 'lucide-react';
import type { CleaningTaskQueueResponse } from '@/shared/api';
import { Button } from '@/shared/components';
import { STEP_LABEL } from '../my-tasks/myTasksModel';
import { clockTime } from '../my-tasks/taskLabels';
import type { WorkStep } from './workModel';

// Am Handy bis an die Leiste unten (über das Polster des Inhalts), ab Tablet als schwebende Karte.
const SHELL =
  'border-hairline sticky -bottom-4 z-10 -mx-4 mt-auto flex flex-col gap-1.5 border-t bg-white px-4 pb-4 pt-3 md:bottom-0 md:mx-0 md:rounded-tile md:border md:shadow-float';

/**
 * Der feste Fuß der Aufgabe (Board B): mit Fotos der Satz, welche Räume noch fehlen; ohne Fotos der
 * große Knopf „Erledigt melden“; erledigt oder entfallen der Endstand. Ein gescheiterter Schritt
 * steht immer hier, auch wenn der neu geladene Stand keinen Knopf mehr anbietet.
 */
export function WorkFooter({
  task,
  step,
  openRooms,
  online,
  busy,
  failure,
  timeZone,
  onStep,
}: {
  task: CleaningTaskQueueResponse;
  step: WorkStep | null;
  openRooms: string | null;
  online: boolean;
  busy: boolean;
  failure: string | null;
  timeZone?: string;
  onStep: (step: WorkStep) => void;
}) {
  const alert = failure && (
    <p role="alert" className="text-sealwax text-sm font-semibold">
      {failure}
    </p>
  );
  if (task.status === 'Cancelled')
    return (
      <footer className={SHELL}>
        <b className="text-sm">Entfällt – die Buchung dahinter gibt es nicht mehr.</b>
        <span className="text-ink-soft text-xs">Hier ist nichts mehr zu tun.</span>
      </footer>
    );
  if (task.status === 'Completed' || task.status === 'Issue')
    return (
      <footer className={SHELL}>
        {alert}
        <b className="text-forest flex items-center gap-1.5 text-sm">
          <Check size={17} strokeWidth={2.5} aria-hidden />
          {task.status === 'Issue' ? 'Erledigt mit offener Meldung' : 'Erledigt'}
          {task.completedAt && ` um ${clockTime(task.completedAt, timeZone)}`}
        </b>
      </footer>
    );
  if (!step)
    return openRooms || alert ? (
      <footer className={SHELL}>
        {alert}
        {openRooms && <b className="text-sm">{openRooms}</b>}
        {openRooms && (
          <span className="text-ink-soft text-xs">
            {online
              ? 'Mit dem letzten Foto ist die Aufgabe erledigt.'
              : 'Fotos gehen erst wieder mit Netz.'}
          </span>
        )}
      </footer>
    ) : null;
  const finish = step === 'finish';
  return (
    <footer className={SHELL}>
      <Button
        variant="brand"
        className="rounded-tile h-[60px] w-full text-[17px] font-extrabold"
        leftIcon={finish ? <Check size={22} strokeWidth={2.5} aria-hidden /> : undefined}
        loading={busy}
        disabled={!online || busy}
        onClick={() => onStep(step)}
      >
        {STEP_LABEL[step]}
      </Button>
      {alert}
      <span className="text-ink-soft text-xs leading-relaxed">
        {!online
          ? 'Gesperrt, bis die Verbindung zurück ist.'
          : finish
            ? 'Mit offenem Schaden steht sie danach auf „Erledigt mit Meldung“ – das entscheidet der Server.'
            : openRooms
              ? `${openRooms}. Mit dem letzten Foto ist die Aufgabe erledigt.`
              : 'Danach nimmst du die Fotos je Raum auf.'}
      </span>
    </footer>
  );
}
